/**
 * Superannuation Contribution Engine
 * 
 * Models complex contribution strategies with Australian regulatory compliance:
 * - Superannuation Guarantee (SG) contributions
 * - Salary sacrifice (concessional contributions)
 * - Non-concessional contributions
 * - Contribution caps and carry-forward rules
 * - Co-contributions and government incentives
 * - Contributions tax (15% concessional, Div 293)
 */

import { SuperannuationCaps2025 } from '../superannuation/SuperannuationCaps2025';
import { ADTScenario } from '@/types/adt';
import { logger } from '@/lib/utils/logger';

export interface SuperannuationContributionResult {
  employerSG: number;
  voluntaryConcessional: number;
  nonConcessional: number;
  carryForward: number;
  spouseContribution: number;
  governmentCoContribution: number;
  total: number;
  taxOnContributions: number;
  division293Tax: number;
  effectiveContribution: number; // After tax
  cappingApplied: boolean;
  warnings: string[];
}

export interface ContributionHistory {
  year: number;
  concessionalUsed: number;
  nonConcessionalUsed: number;
  unusedConcessionalCap: number;
}

export class SuperannuationContributionEngine {
  private contributionHistory: ContributionHistory[] = [];
  
  /**
   * Calculate comprehensive annual superannuation contributions
   */
  static calculateAnnualContributions(
    scenario: ADTScenario,
    year: number,
    currentAge: number,
    grossIncome: number,
    totalSuperBalance: number
  ): SuperannuationContributionResult {
    const superCaps = SuperannuationCaps2025.getSuperannuationCaps();
    const warnings: string[] = [];
    
    // 1. Calculate Superannuation Guarantee (employer contribution)
    const sgRate = 0.115; // 11.5% for 2024-25
    const employerSG = grossIncome * sgRate;
    
    // 2. Get salary sacrifice amount from scenario (voluntary concessional)
    const salarySacrifice = (scenario.superannuationContributions?.voluntaryConcessional || 0) * 12;
    
    // 3. Calculate total concessional contributions
    let totalConcessional = employerSG + salarySacrifice;
    const concessionalCap = superCaps.concessionalContributionCap; // $30,000 for 2024-25
    
    // 4. Apply carry-forward rules if applicable
    let carryForward = 0;
    const canUseCarryForward = totalSuperBalance < 500000; // TSB must be below $500k
    
    if (canUseCarryForward && totalConcessional > concessionalCap) {
      // Can use unused cap from previous 5 years
      const unusedCapacity = this.calculateUnusedConcessionalCapacity(
        scenario,
        year,
        concessionalCap
      );
      
      if (unusedCapacity > 0) {
        carryForward = Math.min(
          totalConcessional - concessionalCap,
          unusedCapacity
        );
        logger.info(`📊 Using carry-forward: $${carryForward.toLocaleString()}`);
      }
    }
    
    // 5. Cap concessional contributions if exceeded
    let cappingApplied = false;
    let excessConcessional = 0;
    
    if (totalConcessional > concessionalCap + carryForward) {
      excessConcessional = totalConcessional - (concessionalCap + carryForward);
      totalConcessional = concessionalCap + carryForward;
      cappingApplied = true;
      warnings.push(`Concessional cap exceeded by $${excessConcessional.toLocaleString()}. Contributions capped at $${totalConcessional.toLocaleString()}.`);
    }
    
    // 6. Calculate non-concessional contributions
    const nonConcessionalMonthly = scenario.superannuationContributions?.voluntaryNonConcessional || 0;
    let nonConcessional = nonConcessionalMonthly * 12;
    const nonConcessionalCap = superCaps.nonConcessionalContributionCap; // $120,000 for 2024-25
    
    // Handle bring-forward rule (can contribute 3 years' worth if under 75)
    const bringForwardAvailable = currentAge < 75;
    const effectiveNonConcessionalCap = bringForwardAvailable 
      ? nonConcessionalCap * 3 
      : nonConcessionalCap;
    
    if (nonConcessional > effectiveNonConcessionalCap) {
      const excess = nonConcessional - effectiveNonConcessionalCap;
      nonConcessional = effectiveNonConcessionalCap;
      cappingApplied = true;
      warnings.push(`Non-concessional cap exceeded by $${excess.toLocaleString()}. Contributions capped at $${effectiveNonConcessionalCap.toLocaleString()}.`);
    }
    
    // 7. Calculate spouse contribution with eligibility check and tax offset
    const spouseIncome = scenario.partnerIncome?.primaryIncome || 0;
    const spouseContributionEligible = spouseIncome < 40000;
    
    let spouseContribution = 0;
    let spouseContributionOffset = 0;
    
    if (spouseContributionEligible && scenario.superannuationContributions?.spouseContribution) {
      const maxSpouseContribution = 3000;
      spouseContribution = Math.min(
        scenario.superannuationContributions.spouseContribution * 12,
        maxSpouseContribution
      );
      
      // Calculate 18% tax offset (max $540)
      spouseContributionOffset = Math.min(spouseContribution * 0.18, 540);
      
      if (spouseContribution > 0) {
        logger.info(`💑 Spouse contribution: $${spouseContribution.toLocaleString()}, Tax offset: $${spouseContributionOffset.toLocaleString()}`);
      }
    }
    
    // 8. Calculate government co-contribution (for low income earners)
    const governmentCoContribution = this.calculateGovernmentCoContribution(
      grossIncome,
      nonConcessional
    );
    
    // 9. Calculate contributions tax
    // Standard 15% tax on concessional contributions
    const contributionsTax = totalConcessional * 0.15;
    
    // 10. Calculate Division 293 tax (additional 15% if income > $250,000)
    let division293Tax = 0;
    const division293Threshold = 250000;
    
    if (grossIncome > division293Threshold) {
      const excessIncome = grossIncome - division293Threshold;
      const taxableContributions = Math.min(totalConcessional, excessIncome);
      division293Tax = taxableContributions * 0.15;
      
      if (division293Tax > 0) {
        warnings.push(`Division 293 tax applies: $${division293Tax.toLocaleString()} (income over $250,000 threshold)`);
      }
    }
    
    // 11. Calculate totals
    const total = employerSG + salarySacrifice + nonConcessional + 
                  spouseContribution + governmentCoContribution;
    const totalTax = contributionsTax + division293Tax;
    const effectiveContribution = total - totalTax;
    
    return {
      employerSG,
      voluntaryConcessional: salarySacrifice,
      nonConcessional,
      carryForward,
      spouseContribution,
      governmentCoContribution,
      total,
      taxOnContributions: contributionsTax,
      division293Tax,
      effectiveContribution,
      cappingApplied,
      warnings
    };
  }
  
  /**
   * Calculate unused concessional capacity from previous years
   * Uses actual contribution history from dashboard if available
   */
  private static calculateUnusedConcessionalCapacity(
    scenario: ADTScenario,
    currentYear: number,
    currentCap: number
  ): number {
    // Load actual contribution history from dashboard data
    const unusedCaps = scenario.strategy?.criticalDataFoundation?.unusedConcessionalCapHistory || [];
    
    if (unusedCaps.length > 0) {
      // Use actual historical data
      const totalUnused = unusedCaps
        .filter(cap => cap.isAvailable)
        .reduce((sum, cap) => sum + cap.unusedAmount, 0);
      
      logger.info('✅ Using actual contribution history:', {
        years: unusedCaps.length,
        totalUnused,
        breakdown: unusedCaps.map(c => ({ year: c.financialYear, unused: c.unusedAmount }))
      });
      
      return totalUnused;
    } else {
      // Fallback to conservative assumption
      logger.warn('⚠️ No contribution history found, using 50% assumption');
      const historicalYears = Math.min(currentYear, 5); // Max 5 years carry-forward
      const assumedHistoricalUsage = currentCap * 0.5;
      const unusedPerYear = currentCap - assumedHistoricalUsage;
      
      return unusedPerYear * historicalYears;
    }
  }
  
  /**
   * Calculate government co-contribution for low income earners
   */
  private static calculateGovernmentCoContribution(
    income: number,
    nonConcessionalAmount: number
  ): number {
    // 2024-25 thresholds
    const lowerThreshold = 43445;
    const upperThreshold = 58445;
    const maxCoContribution = 500;
    
    if (income >= upperThreshold || nonConcessionalAmount === 0) {
      return 0;
    }
    
    if (income <= lowerThreshold) {
      return Math.min(maxCoContribution, nonConcessionalAmount * 0.5);
    }
    
    // Taper between thresholds
    const incomeAboveLower = income - lowerThreshold;
    const taperRange = upperThreshold - lowerThreshold;
    const taperFactor = 1 - (incomeAboveLower / taperRange);
    const eligibleCoContribution = maxCoContribution * taperFactor;
    
    return Math.min(eligibleCoContribution, nonConcessionalAmount * 0.5);
  }
  
  /**
   * Calculate optimal contribution strategy for tax minimization
   */
  static optimizeContributionStrategy(
    currentAge: number,
    grossIncome: number,
    currentSuperBalance: number,
    availableCashFlow: number,
    targetRetirementAge: number
  ): {
    recommendedSalarySacrifice: number;
    recommendedNonConcessional: number;
    estimatedTaxSaving: number;
    reasoning: string[];
  } {
    const superCaps = SuperannuationCaps2025.getSuperannuationCaps();
    const reasoning: string[] = [];
    
    // Calculate current SG contribution
    const sgContribution = grossIncome * 0.115;
    const remainingConcessionalCap = superCaps.concessionalContributionCap - sgContribution;
    
    // Estimate marginal tax rate
    const marginalRate = grossIncome > 180000 ? 0.47 :
                         grossIncome > 120000 ? 0.39 :
                         grossIncome > 45000 ? 0.325 : 0.19;
    
    // Calculate tax benefit of salary sacrifice (marginal rate - 15%)
    const taxBenefit = marginalRate - 0.15;
    
    // Optimize concessional contributions
    let recommendedSalarySacrifice = 0;
    if (taxBenefit > 0 && availableCashFlow > 0) {
      recommendedSalarySacrifice = Math.min(
        availableCashFlow * 0.5, // Use up to 50% of available cash
        remainingConcessionalCap
      );
      reasoning.push(`Salary sacrifice recommended: saves ${(taxBenefit * 100).toFixed(1)}% in tax`);
    }
    
    // Optimize non-concessional contributions
    let recommendedNonConcessional = 0;
    const remainingCashFlow = availableCashFlow - recommendedSalarySacrifice;
    const TBC_2025 = 1900000;
    const canUseNonConcessional = currentSuperBalance < TBC_2025; // Under TBC
    
    // Check if approaching Transfer Balance Cap
    if (currentSuperBalance > TBC_2025 * 0.9) {
      reasoning.push(
        `⚠️ Super balance ($${currentSuperBalance.toLocaleString()}) approaching Transfer Balance Cap ($${TBC_2025.toLocaleString()}). ` +
        `Consider non-super investment strategies to avoid breaching cap.`
      );
    }
    
    if (currentSuperBalance >= TBC_2025) {
      reasoning.push(
        `🚫 Super balance exceeds Transfer Balance Cap. Non-concessional contributions not permitted.`
      );
    }
    
    if (canUseNonConcessional && remainingCashFlow > 0) {
      const nonConcessionalCap = superCaps.nonConcessionalContributionCap;
      recommendedNonConcessional = Math.min(
        remainingCashFlow,
        nonConcessionalCap
      );
      reasoning.push(`Non-concessional contributions recommended: tax-free growth in super`);
    }
    
    // Calculate estimated tax saving
    const estimatedTaxSaving = recommendedSalarySacrifice * taxBenefit;
    
    return {
      recommendedSalarySacrifice,
      recommendedNonConcessional,
      estimatedTaxSaving,
      reasoning
    };
  }
}
